import {
  Stack,
  Grid,
  Typography,
  TextField,
  Checkbox,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useCallback, useEffect, useState } from 'react';
import Image from 'src/components/image';
import PropTypes from 'prop-types';
import Iconify from 'src/components/iconify';
import {
  checkInviteUser,
  confirmInviteUser,
  getPublicOrganizationDetails,
} from 'src/services/user.service';
import { usePathname, useRouter } from 'src/routes/hooks';
import { LoadingScreen } from 'src/components/loading-screen';
import TermsAndPolicy from 'src/sections/terms-and-privacy';
import { useResponsive } from 'src/hooks/use-responsive';

export default function TeamInviteView({ orgId, userId, token }) {
  // hooks
  const router = useRouter();
  const pathname = usePathname();
  const mdUp = useResponsive('up', 'md');

  // states
  const [isLoading, setIsLoading] = useState(true);
  const [orgName, setOrgName] = useState('');
  const [orgLogo, setOrgLogo] = useState('');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [currentStep, setCurrentStep] = useState('NAME');
  const [formErrors, setFormErrors] = useState({});
  const [isAccepting, setIsAccepting] = useState(false);
  const [termsAndPolicyType, setTermsAndPolicyType] = useState('');
  const [isChecked, setIsChecked] = useState(true);

  // functions
  const handleOnClick = async () => {
    if (currentStep === 'NAME') {
      if (name.length <= 0) {
        setFormErrors({ name: 'Name is required' });
      } else {
        setFormErrors({ name: '' });
        setCurrentStep('PASSWORD');
      }
    } else if (currentStep === 'PASSWORD') {
      if (password.length <= 0) {
        setFormErrors({ password: 'Password is required' });
      } else if (confirmPassword.length <= 0) {
        setFormErrors({ confirmPassword: 'Confirm Password is required' });
      } else if (password !== confirmPassword) {
        setFormErrors({ confirmPassword: 'Passwords do not match' });
      } else if (password.length < 4) {
        setFormErrors({ password: 'Password must be at least 4 characters' });
      } else {
        setFormErrors({ password: '', confirmPassword: '' });
        try {
          setIsAccepting(true);

          const payload = {
            name,
            password,
          };

          const response = await confirmInviteUser(
            orgId,
            userId,
            payload,
            token,
          );

          if (response.success) {
            router.push(`${pathname}/success?token=${token}`);
          } else throw new Error(response.message);
        } catch (err) {
          console.error(err);
        } finally {
          setIsAccepting(false);
        }
      }
    }
  };

  const checkUserInvite = useCallback(async () => {
    try {
      const response = await checkInviteUser(orgId, userId, token);

      if (response.success) {
        const isValid = response.body.team_invite_link_valid;

        if (!isValid) {
          router.push(`${pathname}/expired?token=${token}`);
        }
      } else if (response?.response?.status === 401) {
        router.push(`/team-invite/${orgId}/${userId}/expired?token=${token}`);
      }
    } catch (err) {
      console.error(err);
    }
  }, [orgId, pathname, router, token, userId]);

  const getOrg = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await getPublicOrganizationDetails(orgId);

      if (response.success) {
        setOrgName(response.body.name);
        setOrgLogo(response.body.logo_url);
      } else {
        throw new Error(response.message);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  }, [orgId]);

  // effects
  useEffect(() => {
    (async () => {
      await checkUserInvite();
      getOrg();
    })();
  }, [checkUserInvite, getOrg]);

  return (
    <Grid container height="100vh" minHeight="100vh" maxHeight="100vh">
      {!isLoading ? (
        <>
          <Stack
            component="header"
            direction="row"
            alignItems="center"
            justifyContent="center"
            padding="10px"
            borderBottom="1px solid #E9E9E9"
            bgcolor="background.primaryWhite1"
            position="fixed"
            top="0"
            zIndex="99"
            height={{ xs: '60px', md: '80px' }}
            width="100%"
            display={{
              xs: 'flex',
              md: 'none',
            }}
          >
            <Stack alignItems="center">
              <Image
                sx={{
                  width: { xs: '130px', md: '175px' },
                }}
                src={orgLogo}
                alt="Company Logo"
              />
            </Stack>
          </Stack>
          <Grid
            display={{
              xs: 'none',
              md: 'flex',
            }}
            item
            xs={6}
          >
            <Stack
              spacing="40px"
              alignItems="center"
              justifyContent="center"
              bgcolor="#000000"
              height="100%"
              pt="50px"
              pb="20px"
              px="40px"
            >
              <Stack spacing="16px">
                <Typography
                  variant="h1"
                  color="content.primaryWhite"
                  fontSize={44}
                >
                  Welcome!!
                </Typography>
                <Typography
                  variant="paragraph"
                  fontSize={{
                    md: '18px',
                  }}
                  color="background.primaryGrey2"
                  fontWeight={500}
                  lineHeight="normal"
                >
                  Your all-in-one hiring platform to screen, interview, and hire
                  candidates.
                </Typography>
              </Stack>
              <Image
                sx={{ width: '65%' }}
                src="/assets/illustrations/auth.svg"
                alt="auth-illustration"
              />
            </Stack>
          </Grid>
          <Grid item md={6} xs={12}>
            <Stack
              alignItems={{ xs: 'start', md: 'center' }}
              justifyContent={{ xs: 'start', md: 'center' }}
              height="100%"
              pt={{ xs: '80px', md: '50px' }}
              pb="20px"
              px={{ xs: '16px', md: '40px' }}
              bgcolor="background.primaryWhite1"
            >
              <Stack width={{ xs: '100%', md: '80%' }}>
                <Typography
                  textAlign="left"
                  variant={mdUp ? 'h2' : 'h3'}
                  mb={{ xs: '20px', md: '32px' }}
                >
                  Join {orgName}
                </Typography>
                <Stack mb="24px" spacing={{ xs: '20px', md: '24px' }}>
                  {currentStep === 'NAME' && (
                    <Stack spacing="6px">
                      <Typography
                        variant="subheading2"
                        fontSize={{
                          md: '16px',
                        }}
                        fontWeight={600}
                      >
                        Name
                      </Typography>
                      <TextField
                        placeholder="e.g. Antony David"
                        value={name}
                        onChange={e => setName(e.target.value)}
                        error={!!formErrors?.name}
                        helperText={formErrors?.name}
                      />
                    </Stack>
                  )}
                  {currentStep === 'PASSWORD' && (
                    <>
                      <Stack spacing="6px">
                        <Typography variant="subheading2" fontWeight={600}>
                          Password
                        </Typography>
                        <TextField
                          placeholder="e.g. Password"
                          type={showPassword ? 'text' : 'password'}
                          value={password}
                          onChange={e => setPassword(e.target.value)}
                          error={!!formErrors?.password}
                          helperText={formErrors?.password}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  onClick={() => setShowPassword(prev => !prev)}
                                >
                                  <Iconify
                                    icon={
                                      showPassword
                                        ? 'tabler:eye'
                                        : 'tabler:eye-closed'
                                    }
                                  />
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Stack>
                      <Stack spacing="6px">
                        <Typography variant="subheading2" fontWeight={600}>
                          Confirm Password
                        </Typography>
                        <TextField
                          placeholder="e.g. Confirm Password"
                          type={showConfirmPassword ? 'text' : 'password'}
                          value={confirmPassword}
                          onChange={e => setConfirmPassword(e.target.value)}
                          error={!!formErrors?.confirmPassword}
                          helperText={formErrors?.confirmPassword}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  onClick={() =>
                                    setShowConfirmPassword(prev => !prev)
                                  }
                                >
                                  <Iconify
                                    icon={
                                      showConfirmPassword
                                        ? 'tabler:eye'
                                        : 'tabler:eye-closed'
                                    }
                                  />
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Stack>
                    </>
                  )}
                </Stack>
                <Stack
                  width={{
                    xs: '100%',
                    md: 'auto',
                  }}
                >
                  {currentStep === 'NAME' && (
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing="6px"
                      mb={{ xs: '16px', md: '14px' }}
                    >
                      <Checkbox
                        defaultChecked
                        value={isChecked}
                        onChange={e => {
                          setIsChecked(e.target.checked);
                        }}
                      />
                      <Typography
                        variant={mdUp ? 'input' : 'caption'}
                        component="span"
                      >
                        I agree to{' '}
                        <Typography
                          variant={mdUp ? 'input' : 'caption'}
                          component="span"
                          sx={{
                            cursor: 'pointer',
                            color: 'content.accent1',
                          }}
                          onClick={() => {
                            setTermsAndPolicyType('TERMS_AND_CONDITIONS');
                          }}
                        >
                          Terms of use
                        </Typography>{' '}
                        &{' '}
                        <Typography
                          variant={mdUp ? 'input' : 'caption'}
                          component="span"
                          sx={{
                            cursor: 'pointer',
                            color: 'content.accent1',
                          }}
                          onClick={() => {
                            setTermsAndPolicyType('PRIVACY_POLICY');
                          }}
                        >
                          Privacy Policy
                        </Typography>
                      </Typography>
                    </Stack>
                  )}
                  <LoadingButton
                    loading={isAccepting}
                    disabled={!isChecked}
                    variant="contained"
                    fullWidth
                    onClick={handleOnClick}
                  >
                    Continue
                  </LoadingButton>
                </Stack>
              </Stack>
            </Stack>
          </Grid>
          <TermsAndPolicy
            open={termsAndPolicyType}
            handleOnClose={() => {
              setTermsAndPolicyType('');
            }}
            type={termsAndPolicyType}
          />
        </>
      ) : (
        <LoadingScreen />
      )}
    </Grid>
  );
}

TeamInviteView.propTypes = {
  orgId: PropTypes.string,
  userId: PropTypes.string,
  token: PropTypes.string,
};
